import CorporateFareRoundedIcon from '@mui/icons-material/CorporateFareRounded'
import PaymentIcon from '@mui/icons-material/PaymentRounded'
import PersonIcon from '@mui/icons-material/PersonRounded'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'
import usePrimaryLicense from '../util/auth/usePrimaryLicense'
import useUser from '../util/auth/useUser'
import { getCNAIUrl } from '../util/cnaiUrl'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import { Divider } from '@mui/material'
import logout from '../util/auth/logout'
import { fireAndForget } from '@counsel-project/client-utils'

export type AccountButtonProps = {
  disabled?: boolean
}

const AccountButton = ({ disabled }: AccountButtonProps) => {
  const theme = useTheme()
  const [user] = useUser()

  const [primaryLicense, , populatePrimaryLicense] = usePrimaryLicense()

  const username = user?.firstName ? `${user?.firstName} ${user?.lastName}`.trim() : user?.email
  const alt = user?.firstName?.[0] ?? user?.email?.[0] ?? 'A'
  const picture = user?.picture
  const email = user?.email

  const [accountOpen, setAccountOpen] = useState(false)
  const accountButtonEl = useRef<HTMLAnchorElement>(null)

  const handleClickAccountButton = (e: React.MouseEvent) => {
    e.preventDefault()
    if (disabled) return
    setAccountOpen(true)
  }

  const handleCloseAccountMenu = () => {
    setAccountOpen(false)
  }

  const openCNAIInNewTab = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    handleCloseAccountMenu()
    window.open(getCNAIUrl(path), '_blank')
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      populatePrimaryLicense().catch(console.error)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [populatePrimaryLicense])

  return (
    <>
      <Box
        onClick={handleClickAccountButton}
        ref={accountButtonEl}
        id="account-button"
        data-qa-id="nav-account-button"
        component="a"
        href="#open-profile-menu"
        sx={{
          userSelect: 'none',
          height: 40,
          width: 40,
          backgroundColor: alpha(theme.palette.primary.main, 0.075),
          borderRadius: 200,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          justifyContent: 'center',
          textDecoration: 'none',
          ':hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
          },
        }}
      >
        <Avatar
          alt={alt}
          src={picture ?? undefined}
          sx={(theme) => ({ bgcolor: theme.palette.primary.main, width: 26, height: 26 })}
        />
      </Box>
      <Menu
        aria-expanded={accountOpen ? 'true' : undefined}
        id="account-menu"
        anchorEl={accountButtonEl.current}
        open={accountOpen}
        onClose={handleCloseAccountMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiMenu-paper': {
            backgroundColor: theme.palette.background.default,
            boxShadow: theme.shadows[24],
          },
        }}
      >
        <Typography sx={{ px: 2, pt: 1 }} variant="body2" fontWeight={500}>
          {username}
        </Typography>
        {username !== email && (
          <Typography sx={{ px: 2 }} variant="subtitle1" color="text.secondary" fontWeight={500}>
            {email}
          </Typography>
        )}
        {primaryLicense ? (
          <Chip
            id="active-license-chip"
            label={`${primaryLicense.type} Membership`}
            color="primary"
            sx={{ mx: 2, mb: 1, mt: 1, fontWeight: 600, fontSize: '0.85rem' }}
          />
        ) : (
          <Chip
            id="no-license-chip"
            label="No License Active"
            color="error"
            sx={{ mx: 2, mb: 1, mt: 1, fontWeight: 600, fontSize: '0.85rem' }}
          />
        )}
        {[
          {
            id: 'account-nav-profile',
            path: '/profile',
            label: 'My Profile',
            Icon: PersonIcon,
            qa: 'nav-profile-menuitem',
          },
          {
            id: 'account-nav-subscriptions',
            path: '/subscriptions',
            label: 'Billing',
            Icon: PaymentIcon,
            qa: 'nav-billing-menuitem',
          },
          {
            id: 'account-nav-organization',
            path: '/organization',
            label: 'My Organization',
            Icon: CorporateFareRoundedIcon,
            qa: 'nav-organization-menuitem',
          },
        ].map(({ id, path, label, Icon, qa }) => (
          <MenuItem
            key={id}
            id={id}
            data-qa-id={qa}
            color="primary"
            LinkComponent="a"
            href={getCNAIUrl(path)}
            onClick={openCNAIInNewTab(path)}
          >
            <Icon sx={{ mr: 1 }} color="primary" />
            <Typography color="primary">{label}</Typography>
          </MenuItem>
        ))}
        <Divider sx={{ my: 1, mx: 2 }} />

        <MenuItem
          id="account-nav-logout"
          data-qa-id="nav-logout-menuitem"
          onClick={() => fireAndForget(logout)}
        >
          <LogoutRoundedIcon sx={{ mr: 1 }} color="error" />
          <Typography color="error">Logout</Typography>
        </MenuItem>
      </Menu>
    </>
  )
}

export default AccountButton
